import { useNavigate } from 'react-router-dom'
import { formatPhoneNumber } from 'react-phone-number-input'
import moment from 'moment'
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons'
import { faSuitcase } from '@fortawesome/pro-solid-svg-icons'
import {
  getCurrentUser,
  getFinancialProfile,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import { UploadDocumentType } from '@/constants/businessConstants'
import { getLegalEntityName } from '@/constants/onboardingConstants'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { formatEin } from '@/features/financial-insights/shared/utils'

import { Text, Icon } from '@/ui'
import { TypographyStyles } from '@/styles'
import { PracticeProfileEntry } from './PracticeProfileEntry'
import {
  CP261Tooltip,
  EinLetterTooltip,
  Form2553Tooltip,
  IncorporationDocTooltip,
  StateRegistrationTooltip,
} from './PracticeProfileTooltips'
import { Form2553UploadItem } from './shared/Form2553UploadItem'
import { FormCP261UploadItem } from './shared/FormCP261UploadItem'
import { FormIncorporationDocumentUploadItem } from './shared/FormIncorporationDocumentUploadItem'
import { FormEinLetterUploadItem } from './shared/FormEinLetterUploadItem'
import { FormStateRegistrationUploadItem } from './shared/FormStateRegistrationUploadItem'

export const YourBusinessSection = () => {
  const navigate = useNavigate()
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const user = useReselector(getCurrentUser)
  const fp = useReselector(getFinancialProfile)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-row gap-2 items-center">
          <Icon
            icon={faSuitcase}
            backgroundStyle="rounded"
            backgroundColor="emphasis"
            size="sm"
          />
          <Text variant={TypographyStyles.LABEL_MD}>Your Business</Text>
        </div>
        <Icon
          icon={faPenToSquare}
          onClick={() => navigate('/practice/profile/editBusiness')}
        />
      </div>

      <PracticeProfileEntry label="Business Name" value={fp?.businessName} />

      <PracticeProfileEntry
        label="Business Entity"
        value={getLegalEntityName(fp?.businessEntity)}
      />
      <PracticeProfileEntry
        label="Business EIN Number"
        value={formatEin(fp?.einNumber)}
      />
      <PracticeProfileEntry
        label="Year Practice Started"
        value={fp?.yearPracticeStarted}
      />
      <div className="h-[24px]" />
      <PracticeProfileEntry
        label="Business Address"
        value={fp?.businessAddress}
      />
      <PracticeProfileEntry label="State of Residence" value={fp?.homeState} />
      {isScorp && (
        <>
          <PracticeProfileEntry
            label="Business Registration State"
            value={fp?.scorpRegistrationState}
          />
          <PracticeProfileEntry
            label="S Corp Election Date"
            value={
              fp?.entityChangeElectionDate &&
              moment
                .utc(fp.entityChangeElectionDate)
                .format(DATE_FORMATS.DISPLAY_LONG)
            }
          />
        </>
      )}
      <PracticeProfileEntry
        label="Business Phone"
        value={user?.phoneNumber && formatPhoneNumber(user?.phoneNumber)}
      />
      <PracticeProfileEntry label="Business Email" value={user?.email} />

      {isScorp ? (
        <>
          <PracticeProfileEntry
            label="Form 2553"
            popupContent={<Form2553Tooltip />}
            documentCat={UserDocumentCategoryIdentifier.form2553}
            uploadItem={<Form2553UploadItem uploadButtonOnly />}
          />
          <PracticeProfileEntry
            label="CP261 Letter"
            popupContent={<CP261Tooltip />}
            documentCat={UserDocumentCategoryIdentifier.cp261Notice}
            uploadItem={<FormCP261UploadItem uploadButtonOnly />}
          />
          <PracticeProfileEntry
            label="Incorporation Document"
            popupContent={<IncorporationDocTooltip />}
            documentCat={UserDocumentCategoryIdentifier.businessIncorporation}
            documentType={UploadDocumentType.INCORPORATION}
            uploadItem={
              <FormIncorporationDocumentUploadItem uploadButtonOnly />
            }
          />
        </>
      ) : (
        <>
          <PracticeProfileEntry
            label="EIN Letter"
            popupContent={<EinLetterTooltip />}
            documentCat={UserDocumentCategoryIdentifier.einDocuments}
            uploadItem={<FormEinLetterUploadItem uploadButtonOnly />}
          />
          <PracticeProfileEntry
            label="State Registration"
            popupContent={<StateRegistrationTooltip />}
            documentCat={UserDocumentCategoryIdentifier.stateRegistration}
            documentType={UploadDocumentType.INCORPORATION}
            uploadItem={<FormStateRegistrationUploadItem uploadButtonOnly />}
          />
        </>
      )}
    </div>
  )
}
